import React from 'react'

import { buildFilterControlProps } from 'features/catalog/hooks/catalog-filter-sidebar-handlers'
import {
  useCatalogFiltersStore,
  NO_GHOST_FILTERS,
} from 'features/catalog/store'
import type { FilterControlProps } from 'features/charts-v2'
// bully ignore[no-cross-feature-private-imports: sibling query/orchestrator hook imports are the team's de facto cross-feature public seam — same ignore Feedback/Keywords already carry on this exact import. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md (reconcile the rule with practice).]
import { useDefaultFilterListLoading } from 'features/filters/hooks/use-default-filter-list-loading'
// bully ignore[no-cross-feature-private-imports: sibling query/orchestrator hook imports are the team's de facto cross-feature public seam — same ignore Feedback/Keywords already carry on this exact import. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md (reconcile the rule with practice).]
import { useEffectiveFilterList } from 'features/filters/hooks/use-effective-filter-list'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { useProjectStore } from 'features/project/store'
import { useCurrentRoute } from 'hooks'
import type { ProjectState } from 'types/project'

/** Full prop set `CatalogFilterSidebar` needs to render `ControlsSidebar`'s
 *  `filter` section; `null` when there's no panel to edit yet. Handler
 *  bodies live in the sibling `catalog-filter-sidebar-handlers` module so
 *  this hook stays thin — it only gathers store state. Mirrors
 *  `use-feedback-filter-sidebar.ts`, minus the panel-switcher concept
 *  (catalog has exactly one panel). */
export function useCatalogFilterSidebar(): FilterControlProps | null {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const { surface } = useCurrentRoute()
  const panels = useCatalogFiltersStore((state) => state.panels)
  const updatePanel = useCatalogFiltersStore((state) => state.updatePanel)
  const updateFilterSelection = useCatalogFiltersStore(
    (state) => state.updateFilterSelection,
  )
  const disableFilter = useCatalogFiltersStore((state) => state.disableFilter)
  const enableFilter = useCatalogFiltersStore((state) => state.enableFilter)
  const dropGhost = useCatalogFiltersStore((state) => state.dropGhost)
  const ghostFiltersByPanel = useCatalogFiltersStore(
    (state) => state.ghostFilters,
  )
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )

  const panel = panels[0] ?? null
  const ghostFilters = panel
    ? (ghostFiltersByPanel[panel.id] ?? NO_GHOST_FILTERS)
    : NO_GHOST_FILTERS

  // Same lazy, single-field-scoped counts fetch ChartControlsSidebar/Feedback
  // use — passing the full field list is the documented ~20x ClickHouse
  // perf cliff (charts-v2/AGENTS.md).
  const [fieldForCounts, setFieldForCounts] = React.useState<string | null>(
    null,
  )
  const countFields = React.useMemo(
    () => (fieldForCounts ? [fieldForCounts] : []),
    [fieldForCounts],
  )
  const filterOptions = useEffectiveFilterList({
    projectId,
    panel,
    defaultFilterList,
    fields: countFields,
  })
  const isFilterOptionsLoading = useDefaultFilterListLoading(projectId)

  if (!panel) return null

  return buildFilterControlProps({
    panel,
    ghostFilters,
    filterOptions,
    isFilterOptionsLoading,
    surface,
    setFieldForCounts,
    actions: {
      updateFilterSelection,
      updatePanel,
      enableFilter,
      disableFilter,
      dropGhost,
    },
  })
}
